import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  Play, Pause, Save, Trash2, Plus, ArrowRight, Settings2, X,
  Zap, Database, Brain, GitBranch, FileText, Bell, Clock, Grip
} from 'lucide-react';

const API = '/api';

// Block type definitions
const BLOCK_TYPES = {
  trigger: {
    icon: Clock,
    label: 'Trigger',
    color: 'bg-blue-500',
    description: 'Start workflow on schedule or event'
  },
  data_fetch: {
    icon: Database,
    label: 'Data Fetch',
    color: 'bg-emerald-500',
    description: 'Fetch data from Orka or external sources'
  },
  ai_process: {
    icon: Brain,
    label: 'AI Process',
    color: 'bg-violet-500',
    description: 'Process data with AI (summarize, analyze, generate)'
  },
  decision: {
    icon: GitBranch,
    label: 'Decision',
    color: 'bg-amber-500',
    description: 'Branch based on conditions'
  },
  execute: {
    icon: Zap,
    label: 'Execute',
    color: 'bg-red-500',
    description: 'Execute an action (create task, send notification)'
  },
  artifact: {
    icon: FileText,
    label: 'Artifact',
    color: 'bg-indigo-500',
    description: 'Create a document or report'
  },
  notify: {
    icon: Bell,
    label: 'Notify',
    color: 'bg-pink-500',
    description: 'Send notification to deck or external channel'
  }
};

// Capability options for blocks
const DATA_FETCH_OPTIONS = [
  { value: 'orka.tasks.list', label: 'List Tasks' },
  { value: 'orka.calendar.list_events', label: 'List Calendar Events' },
  { value: 'orka.analytics.summary', label: 'Get Analytics Summary' }
];

const EXECUTE_OPTIONS = [
  { value: 'orka.tasks.create', label: 'Create Task' },
  { value: 'orka.tasks.update', label: 'Update Task' },
  { value: 'orka.deck.add_item', label: 'Add to Deck' }
];

// ==================== WORKFLOW BLOCK COMPONENT ====================

const WorkflowBlock = ({ block, index, onUpdate, onDelete, onConnect, isSelected, onSelect, canConnect }) => {
  const blockType = BLOCK_TYPES[block.block_type] || BLOCK_TYPES.execute;
  const Icon = blockType.icon;

  return (
    <div
      className={cn(
        "relative bg-background border-2 rounded-lg p-4 w-64 cursor-pointer orka-transition-colors",
        isSelected ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/50",
        "group"
      )}
      onClick={() => onSelect(block.block_id)}
      draggable
      data-testid={`workflow-block-${block.block_id}`}
    >
      {/* Drag handle */}
      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-50 cursor-grab">
        <Grip className="w-4 h-4" />
      </div>

      {/* Delete button */}
      <button
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive/80"
        onClick={(e) => { e.stopPropagation(); onDelete(block.block_id); }}
      >
        <X className="w-4 h-4" />
      </button>

      {/* Block header */}
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-white", blockType.color)}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-medium">{block.name || blockType.label}</div>
          <div className="text-xs text-muted-foreground">{blockType.label}</div>
        </div>
      </div>

      {/* Block config preview */}
      {block.capability_id && (
        <div className="text-xs bg-muted px-2 py-1 rounded mb-2">
          {block.capability_id}
        </div>
      )}

      {/* Risk tier badge */}
      {block.risk_tier && block.risk_tier !== 'low' && (
        <Badge 
          variant="outline" 
          className={cn(
            "text-xs",
            block.risk_tier === 'medium' && "border-amber-200 text-amber-600",
            block.risk_tier === 'high' && "border-red-200 text-red-600"
          )}
        >
          {block.risk_tier} risk
        </Badge>
      )}

      {/* Connection point */}
      {canConnect && (
        <button
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-110 transition-transform"
          onClick={(e) => { e.stopPropagation(); onConnect(block.block_id); }}
        >
          <ArrowRight className="w-3 h-3" />
        </button>
      )}

      {/* Index badge */}
      <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs font-medium">
        {index + 1}
      </div>
    </div>
  );
};

// ==================== BLOCK CONFIG PANEL ====================

const BlockConfigPanel = ({ block, onUpdate, onClose, capabilities }) => {
  const [config, setConfig] = useState(block.config || {});
  const [name, setName] = useState(block.name || '');
  const [capabilityId, setCapabilityId] = useState(block.capability_id || '');

  const handleSave = () => {
    onUpdate(block.block_id, {
      ...block,
      name: name || BLOCK_TYPES[block.block_type]?.label,
      capability_id: capabilityId,
      config
    });
    onClose();
    toast.success('Block updated');
  };

  const getCapabilityOptions = () => {
    if (block.block_type === 'data_fetch') return DATA_FETCH_OPTIONS;
    if (block.block_type === 'execute') return EXECUTE_OPTIONS;
    return [];
  };

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-background border-l shadow-xl z-50 flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Configure Block</h3>
          <p className="text-sm text-muted-foreground">{BLOCK_TYPES[block.block_type]?.label}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {/* Block name */}
          <div>
            <label className="text-sm font-medium mb-2 block">Block Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={BLOCK_TYPES[block.block_type]?.label}
            />
          </div>

          {/* Capability selection for data_fetch and execute blocks */}
          {(block.block_type === 'data_fetch' || block.block_type === 'execute') && (
            <div>
              <label className="text-sm font-medium mb-2 block">Capability</label>
              <Select value={capabilityId} onValueChange={setCapabilityId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select capability" />
                </SelectTrigger>
                <SelectContent>
                  {getCapabilityOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Trigger config */}
          {block.block_type === 'trigger' && (
            <>
              <div>
                <label className="text-sm font-medium mb-2 block">Trigger Type</label>
                <Select 
                  value={config.trigger_type || 'manual'} 
                  onValueChange={(v) => setConfig({...config, trigger_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="event">On Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {config.trigger_type === 'scheduled' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Schedule Time</label>
                  <Input
                    type="time"
                    value={config.schedule_time || '08:00'}
                    onChange={(e) => setConfig({...config, schedule_time: e.target.value})}
                  />
                </div>
              )}
            </>
          )}

          {/* AI Process config */}
          {block.block_type === 'ai_process' && (
            <div>
              <label className="text-sm font-medium mb-2 block">AI Operation</label>
              <Select 
                value={config.operation || 'summarize'} 
                onValueChange={(v) => setConfig({...config, operation: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summarize">Summarize</SelectItem>
                  <SelectItem value="analyze">Analyze</SelectItem>
                  <SelectItem value="generate">Generate</SelectItem>
                  <SelectItem value="transform">Transform</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Decision config */}
          {block.block_type === 'decision' && (
            <div>
              <label className="text-sm font-medium mb-2 block">Condition</label>
              <Input
                value={config.condition || ''}
                onChange={(e) => setConfig({...config, condition: e.target.value})}
                placeholder="e.g., tasks.length > 0"
              />
            </div>
          )}

          {/* Notify config */}
          {block.block_type === 'notify' && (
            <div>
              <label className="text-sm font-medium mb-2 block">Notification Channel</label>
              <Select 
                value={config.channel || 'deck'} 
                onValueChange={(v) => setConfig({...config, channel: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deck">The Deck</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="slack">Slack</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </ScrollArea>

      <div className="p-4 border-t">
        <Button onClick={handleSave} className="w-full">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

// ==================== MAIN WORKFLOW EDITOR ====================

const WorkflowEditor = ({ workflow, onSave, onClose, capabilities }) => {
  const [blocks, setBlocks] = useState(workflow?.blocks || []);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [workflowName, setWorkflowName] = useState(workflow?.name || 'New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState(workflow?.description || '');
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);

  const selectedBlock = blocks.find(b => b.block_id === selectedBlockId);

  const addBlock = (blockType) => {
    const newBlock = {
      block_id: `block_${Date.now()}`,
      block_type: blockType,
      name: BLOCK_TYPES[blockType].label,
      config: {},
      capability_id: null,
      risk_tier: 'low',
      position: { x: blocks.length * 50, y: blocks.length * 50 },
      next_blocks: []
    };
    setBlocks([...blocks, newBlock]);
    setShowAddBlock(false);
    toast.success(`${BLOCK_TYPES[blockType].label} block added`);
  };

  const updateBlock = (blockId, updates) => {
    setBlocks(blocks.map(b => b.block_id === blockId ? { ...b, ...updates } : b));
  };

  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter(b => b.block_id !== blockId));
    if (selectedBlockId === blockId) setSelectedBlockId(null);
    toast.success('Block deleted');
  };

  const connectBlocks = (fromBlockId) => {
    // Simple linear connection for now
    const fromIndex = blocks.findIndex(b => b.block_id === fromBlockId);
    if (fromIndex < blocks.length - 1) {
      const toBlock = blocks[fromIndex + 1];
      updateBlock(fromBlockId, {
        next_blocks: [toBlock.block_id]
      });
      toast.success('Blocks connected');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        blocks: blocks
      };
      await onSave(workflowData);
      toast.success('Workflow saved');
    } catch (err) {
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Header */}
      <div className="border-b px-6 py-4 flex items-center justify-between bg-background">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
          <div>
            <Input
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="font-semibold text-lg border-none p-0 h-auto focus-visible:ring-0"
              placeholder="Workflow name"
            />
            <Input
              value={workflowDescription}
              onChange={(e) => setWorkflowDescription(e.target.value)}
              className="text-sm text-muted-foreground border-none p-0 h-auto focus-visible:ring-0"
              placeholder="Add description..."
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{blocks.length} blocks</Badge>
          <Button variant="outline" onClick={() => setShowAddBlock(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Block
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Workflow'}
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={canvasRef} className="flex-1 overflow-auto p-8 bg-muted/30" data-testid="workflow-canvas">
        {blocks.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <GitBranch className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Start Building Your Workflow</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Add blocks to create an automated workflow. Connect them to define the execution flow.
            </p>
            <Button onClick={() => setShowAddBlock(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Block
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-8 items-start">
            {blocks.map((block, index) => (
              <React.Fragment key={block.block_id}>
                <WorkflowBlock
                  block={block}
                  index={index}
                  onUpdate={updateBlock}
                  onDelete={deleteBlock}
                  onConnect={connectBlocks}
                  isSelected={selectedBlockId === block.block_id}
                  onSelect={setSelectedBlockId}
                  canConnect={index < blocks.length - 1}
                />
                {index < blocks.length - 1 && (
                  <div className="flex items-center self-center">
                    <ArrowRight className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Block config panel */}
      {selectedBlock && (
        <BlockConfigPanel
          block={selectedBlock}
          onUpdate={updateBlock}
          onClose={() => setSelectedBlockId(null)}
          capabilities={capabilities}
        />
      )}

      {/* Add block dialog */}
      <Dialog open={showAddBlock} onOpenChange={setShowAddBlock}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Block</DialogTitle>
            <DialogDescription>Choose a block type to add to your workflow</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {Object.entries(BLOCK_TYPES).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  className="flex items-start gap-3 p-4 rounded-lg border hover:border-primary hover:bg-muted/50 transition-colors text-left"
                >
                  <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0", config.color)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium">{config.label}</div>
                    <div className="text-xs text-muted-foreground">{config.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowEditor;
